import React, { useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Breadcrumbs,
  Link,
  useTheme
} from '@mui/material';
import {
  ShoppingCart,
  Link as LinkIcon,
  Settings,
  Sync,
  History
} from '@mui/icons-material';
import ConnectionSetup from './components/ConnectionSetup';
import ConnectionsList from './components/ConnectionsList';
import SyncSettings from './components/SyncSettings';
import SyncStatus from './components/SyncStatus';
import SyncLogs from './components/SyncLogs';
import useShopifyStore from './store/useShopifyStore';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ height: '100%' }}>
    {value === index && (
      <Box sx={{ py: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

const ShopifyPlugin = () => {
  const theme = useTheme();
  const {
    activeTab,
    setActiveTab,
    selectedConnection,
    setSelectedConnection
  } = useShopifyStore();

  const [showSetup, setShowSetup] = useState(false);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddConnection = () => {
    setShowSetup(true);
    setActiveTab('setup');
  };

  const handleConnectionCreated = () => {
    setShowSetup(false);
    setActiveTab('connections');
  };

  const handleSelectConnection = (connection) => {
    setSelectedConnection(connection);
    setActiveTab('settings');
  };

  const handleConnectionUpdate = (updatedConnection) => {
    setSelectedConnection(updatedConnection);
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.default,
        p: 3
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" href="/dashboard">
            Dashboard
          </Link>
          <Link underline="hover" color="inherit" href="/dashboard/plugins">
            Plugins
          </Link>
          <Typography color="text.primary">Shopify Integration</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ShoppingCart sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              Shopify Integration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sync products, inventory, and orders between Shopify and DymenStock
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            value="connections"
            icon={<LinkIcon />}
            label="Connections"
            iconPosition="start"
          />
          {selectedConnection && (
            <>
              <Tab
                value="settings"
                icon={<Settings />}
                label="Settings"
                iconPosition="start"
              />
              <Tab
                value="status"
                icon={<Sync />}
                label="Sync Status"
                iconPosition="start"
              />
              <Tab
                value="logs"
                icon={<History />}
                label="Sync Logs"
                iconPosition="start"
              />
            </>
          )}
          {showSetup && (
            <Tab
              value="setup"
              icon={<ShoppingCart />}
              label="Add Connection"
              iconPosition="start"
            />
          )}
        </Tabs>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <TabPanel value={activeTab} index="connections">
          <ConnectionsList
            onAddConnection={handleAddConnection}
            onSelectConnection={handleSelectConnection}
          />
        </TabPanel>

        <TabPanel value={activeTab} index="settings">
          {selectedConnection && (
            <SyncSettings
              connection={selectedConnection}
              onUpdate={handleConnectionUpdate}
            />
          )}
        </TabPanel>

        <TabPanel value={activeTab} index="status">
          {selectedConnection && (
            <SyncStatus connection={selectedConnection} />
          )}
        </TabPanel>

        <TabPanel value={activeTab} index="logs">
          {selectedConnection && (
            <SyncLogs connection={selectedConnection} />
          )}
        </TabPanel>

        <TabPanel value={activeTab} index="setup">
          <ConnectionSetup onConnectionCreated={handleConnectionCreated} />
        </TabPanel>
      </Box>
    </Box>
  );
};

export default ShopifyPlugin;
